








































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import deck from '@/store/modules/deck';
import { getCollection } from '@/data/gt-library/registry';
import { getLeafComponentLoader } from '@/data/gt-library/leafComponentRegistry';
import {
  filterTreeByNodeIds,
  findNavNodeInPath,
  getResolvedRoots,
  resolveStartState,
} from '@/data/gt-library/treeUtils';
import {
  ContentBlock,
  ResolvedChild,
  TreeCollection,
} from '@/data/gt-library/types';
import PDF from '@/components/simpleSlide/components/PDF.vue';

@Component({
  name: 'ContentTree',
  components: { PDF },
})
export default class ContentTree extends Vue {
  @Prop({ required: true }) readonly collectionId!: string;
  @Prop({ default: undefined }) readonly selectedNodeIds!: string[] | undefined;
  @Prop({ default: undefined }) readonly startNodeId!: string | undefined;
  @Prop({ default: null }) readonly referenceDate!: Date | null;
  @Prop({ default: false }) readonly fullPage!: boolean;
  @Prop({ default: false }) readonly lockNavigation!: boolean;

  pathNav: ResolvedChild[] = [];
  selectedLeaf: ResolvedChild | null = null;
  leafComponent: any = null;

  get collection(): TreeCollection | undefined {
    let col = getCollection(this.collectionId);
    if (!col) {
      return undefined;
    }
    if (this.hasNodeFilter) {
      col = filterTreeByNodeIds(
        col,
        this.selectedNodeIds,
        this.effectiveReferenceDate,
      );
    }
    return col;
  }

  get hasNodeFilter(): boolean {
    return this.selectedNodeIds !== undefined;
  }

  get effectiveReferenceDate(): Date {
    return this.referenceDate || new Date();
  }

  get roots(): ResolvedChild[] {
    if (!this.collection) {
      return [];
    }
    return getResolvedRoots(this.collection, this.effectiveReferenceDate);
  }

  get currentChildren(): ResolvedChild[] {
    if (this.pathNav.length === 0) {
      return this.roots;
    }
    const pathIds = this.pathNav.map((n) => n.id);
    return findNavNodeInPath(this.roots, pathIds);
  }

  get currentLevelName(): string {
    if (this.selectedLeaf) {
      return this.selectedLeaf.title;
    }
    if (this.pathNav.length > 0) {
      return this.pathNav[this.pathNav.length - 1].name;
    }
    return this.collection ? this.collection.name : '';
  }

  get nextStepsEnabled(): boolean {
    if (this.collectionId === 'foretagspresentation-test') {
      return false;
    }
    return deck.Slides.some((slide) => slide.slideType === 'contentNextSteps');
  }

  get isShared(): boolean {
    const route = this.$router.currentRoute.name;
    if (!route) {
      return false;
    }
    return route.startsWith('presentation-shared');
  }

  get isLeafStarred(): boolean {
    if (!this.selectedLeaf) {
      return false;
    }
    const key = this.collectionId + ':' + this.selectedLeaf.id;
    return deck.NextStepsStarred.some(
      (item) => item.collectionId + ':' + item.leafId === key,
    );
  }

  get itemContentClasses(): Record<string, boolean> {
    const isCustom =
      this.selectedLeaf != null && this.selectedLeaf.leafType === 'custom';
    return {
      'item-content': true,
      'item-content-full': isCustom,
      'item-content--full-page': this.fullPage && !isCustom,
    };
  }

  get leafWrapperClasses(): Record<string, boolean> {
    const style =
      (this.selectedLeaf && this.selectedLeaf.leafStyle) || 'standard';
    return {
      'item-wrapper--compact': style === 'compact',
    };
  }

  get isCompactLeaf(): boolean {
    return !!this.selectedLeaf && this.selectedLeaf.leafStyle === 'compact';
  }

  get hasLeafAside(): boolean {
    return this.isCompactLeaf && this.leafAsideContent.length > 0;
  }

  get leafBodyStyle(): Record<string, string> {
    if (!this.hasLeafAside) {
      return {};
    }
    return {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: '1.5cqw',
      width: '100%',
    };
  }

  get firstBoxIndex(): number {
    if (!this.selectedLeaf || !this.selectedLeaf.content) {
      return -1;
    }
    return this.selectedLeaf.content.findIndex((block) => block.type === 'box');
  }

  get leafMainContent(): ContentBlock[] {
    if (!this.selectedLeaf || !this.selectedLeaf.content) {
      return [];
    }
    const idx = this.firstBoxIndex;
    if (idx < 0) {
      return this.selectedLeaf.content;
    }
    return this.selectedLeaf.content.slice(0, idx);
  }

  get leafAsideContent(): ContentBlock[] {
    if (!this.selectedLeaf || !this.selectedLeaf.content) {
      return [];
    }
    const idx = this.firstBoxIndex;
    if (idx < 0) {
      return [];
    }
    return this.selectedLeaf.content.slice(idx);
  }

  blockClass(element: ContentBlock): string {
    if (element.type === 'box') {
      return 'content-box';
    }
    if (element.type === 'pdf') {
      return 'content-pdf';
    }
    return element.type;
  }

  async toggleLeafStar() {
    if (!this.selectedLeaf) {
      return;
    }
    await deck.toggleAndSaveNextStepStar({
      collectionId: this.collectionId,
      leafId: this.selectedLeaf.id,
      name: this.selectedLeaf.name,
    });
  }

  @Watch('selectedLeaf')
  async onSelectedLeafChanged() {
    this.leafComponent = null;
    if (
      !this.selectedLeaf ||
      this.selectedLeaf.leafType !== 'custom' ||
      !this.selectedLeaf.componentId
    ) {
      return;
    }
    const loader = getLeafComponentLoader(this.selectedLeaf.componentId);
    if (!loader) {
      return;
    }
    const module = await loader();
    this.leafComponent = module.default || module;
  }

  mounted() {
    this.applyStartNode();
  }

  @Watch('collectionId')
  onCollectionChanged() {
    this.applyStartNode();
  }

  @Watch('selectedNodeIds')
  onSelectedNodeIdsChanged() {
    this.applyStartNode();
  }

  @Watch('startNodeId')
  onStartNodeIdChanged() {
    this.applyStartNode();
  }

  applyStartNode() {
    const baseCollection = getCollection(this.collectionId);
    if (!baseCollection) {
      this.goToRoot();
      return;
    }

    const effectiveStart = this.startNodeId || this.collectionId;
    const filterIds = this.hasNodeFilter ? this.selectedNodeIds : undefined;
    const state = resolveStartState(
      baseCollection,
      effectiveStart,
      filterIds,
      this.effectiveReferenceDate,
    );
    if (!state) {
      this.goToRoot();
      return;
    }

    if (state.pathNavIds.length === 0 && !state.leafPlacementId) {
      this.goToRoot();
      return;
    }

    const roots = this.roots;
    const pathNav: ResolvedChild[] = [];
    let current = roots;

    for (const id of state.pathNavIds) {
      const nav = current.find((c) => c.id === id && c.kind === 'nav');
      if (!nav || !nav.children) {
        this.goToRoot();
        return;
      }
      pathNav.push(nav);
      current = nav.children;
    }

    this.pathNav = pathNav;
    this.leafComponent = null;

    if (state.leafPlacementId) {
      const leaf = current.find(
        (c) => c.id === state.leafPlacementId && c.kind === 'leaf',
      );
      if (!leaf) {
        this.goToRoot();
        return;
      }
      this.selectedLeaf = leaf;
      return;
    }

    this.selectedLeaf = null;
  }

  truncateName(name: string, max: number): string {
    if (name.length <= max) {
      return name;
    }
    return name.slice(0, max) + '...';
  }

  cardImageStyle(child: ResolvedChild): Record<string, string> {
    if (!child.image) {
      return {};
    }
    return {
      backgroundImage: `url('/static/${child.image}')`,
    };
  }

  heroImageStyle(leaf: ResolvedChild): Record<string, string> {
    const image = leaf.leafImage || leaf.image;
    if (!image) {
      return {};
    }
    return {
      backgroundImage: `url('/static/${image}')`,
    };
  }

  onChildClick(child: ResolvedChild) {
    if (this.lockNavigation) {
      return;
    }
    if (child.kind === 'nav') {
      this.pathNav.push(child);
      this.selectedLeaf = null;
      return;
    }
    this.selectedLeaf = child;
  }

  onRootClick() {
    if (this.lockNavigation) {
      return;
    }
    this.goToRoot();
  }

  onLevelClick(index: number) {
    if (this.lockNavigation) {
      return;
    }
    this.goToLevel(index);
  }

  goToRoot() {
    this.pathNav = [];
    this.selectedLeaf = null;
    this.leafComponent = null;
  }

  goToLevel(index: number) {
    this.pathNav = this.pathNav.slice(0, index + 1);
    this.selectedLeaf = null;
    this.leafComponent = null;
  }
}
